// Stefans-AI-CV-frontend/src/sections/ResearchSection.tsx

import { motion } from 'framer-motion'
import { useI18n } from '../i18n'
import { analyticsService } from '../services/analytics'

interface ResearchItem {
  title: string
  type: string
  date: string
  desc: string
  tags: string[]
  link: string | null
  featured: boolean
}

function FeaturedCard({ item }: { item: ResearchItem }) {
  const trackResearchClick = () => {
    analyticsService.trackEvent('research_link_click', {
      category: 'research',
      label: item.title,
      research_type: item.type,
      is_featured: true,
    })
  }

  return (
    <div className="glass-card rounded-2xl p-6 h-full flex flex-col hover:border-border-strong transition-all duration-300 group hover:-translate-y-1 hover:glow-indigo">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <span className="text-xs font-mono font-semibold text-indigo-400 bg-indigo-dim px-2 py-0.5 rounded-md border border-indigo-400/20 mb-2 inline-block">
            {item.type}
          </span>
          <h3 className="font-heading font-semibold text-text-primary text-base leading-snug group-hover:text-indigo-400 transition-colors">
            {item.title}
          </h3>
        </div>
      </div>

      <p className="text-text-secondary text-sm leading-relaxed flex-1 mb-4">{item.desc}</p>

      <div className="flex flex-wrap gap-1.5 mb-4">
        {item.tags.map((tag, i) => (
          <span key={i} className="px-2 py-0.5 text-xs rounded-md bg-white/5 text-text-muted border border-border">
            {tag}
          </span>
        ))}
      </div>

      <div className="flex items-center justify-between">
        <span className="text-text-muted text-xs font-mono">{item.date}</span>
        {item.link && (
          <a
            href={item.link}
            target="_blank"
            rel="noopener noreferrer"
            onClick={trackResearchClick}
            className="flex items-center gap-1.5 text-indigo-400 text-xs hover:text-indigo-300 transition-colors"
          >
            <span>Read</span>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
              <polyline points="15 3 21 3 21 9" />
              <line x1="10" y1="14" x2="21" y2="3" />
            </svg>
          </a>
        )}
      </div>
    </div>
  )
}

function ListItem({ item }: { item: ResearchItem }) {
  const trackResearchClick = () => {
    analyticsService.trackEvent('research_link_click', {
      category: 'research',
      label: item.title,
      research_type: item.type,
      is_featured: false,
    })
  }

  return (
    <div className="flex items-start gap-4 py-4 border-b border-border last:border-0 group hover:pl-2 transition-all duration-200">
      <div className="flex-shrink-0 pt-0.5">
        <span className="text-xs font-mono text-indigo-400 bg-indigo-dim px-2 py-0.5 rounded-md border border-indigo-400/20">
          {item.type}
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="text-text-primary text-sm font-medium group-hover:text-indigo-400 transition-colors truncate">
          {item.title}
        </h4>
        <p className="text-text-muted text-xs mt-0.5">{item.date}</p>
      </div>
      {item.link && (
        <a
          href={item.link}
          target="_blank"
          rel="noopener noreferrer"
          onClick={trackResearchClick}
          className="flex-shrink-0 text-indigo-400 hover:text-indigo-300 transition-colors"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
            <polyline points="15 3 21 3 21 9" />
            <line x1="10" y1="14" x2="21" y2="3" />
          </svg>
        </a>
      )}
    </div>
  )
}

export default function ResearchSection() {
  const t = useI18n()

  const featuredItems = (t.research.items as ResearchItem[]).filter((i) => i.featured)
  const otherItems = (t.research.items as ResearchItem[]).filter((i) => !i.featured)

  return (
    <section id="research" className="py-24 relative">
      {/* Background */}
      <div className="absolute inset-0 bg-bg-elevated/30" />

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <p className="text-indigo-400 text-sm font-mono mb-2">// research</p>
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary mb-3">
            {t.research.title}
          </h2>
          <p className="text-text-secondary">{t.research.subtitle}</p>
        </motion.div>

        {/* Featured items */}
        <div className="grid md:grid-cols-2 gap-6 mb-10">
          {featuredItems.map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
            >
              <FeaturedCard item={item} />
            </motion.div>
          ))}
        </div>

        {/* Other items list */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="glass-card rounded-2xl p-6"
        >
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-text-secondary text-sm font-mono uppercase tracking-wider">
              {t.research.view_all}
            </h3>
            <a
              href="https://paragraph.com/@aklabs-2"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => analyticsService.trackEvent('research_archive_click', {
                category: 'research',
                label: 'aklabs',
                target_url: 'https://paragraph.com/@aklabs-2',
              })}
              className="text-indigo-400 text-xs hover:text-indigo-300 transition-colors flex items-center gap-1"
            >
              <span>AKLabs</span>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                <polyline points="15 3 21 3 21 9" />
                <line x1="10" y1="14" x2="21" y2="3" />
              </svg>
            </a>
          </div>
          <div>
            {otherItems.map((item, i) => (
              <ListItem key={i} item={item} />
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  )
}
